import type { ForwardRefRenderFunction } from 'react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import * as React from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useReactToPrint } from 'react-to-print';
import { downloadWithLink } from '@hotjar/webapp/lib/downloadWithLink';

export interface MfaRecoveryCodeRef {
  print: () => void;
  download: () => void;
}

interface MfaRecoveryCodeProps {
  recoveryCodes: string[];
}

const RecoveryCodesBox = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  justify-items: center;
  background-color: #f6f6f6;
  margin-top: ${tokens.spacer.x2};
  padding: ${tokens.spacer.x2};

  @media print {
    display: inline-block;
  }
`;

const MfaRecoveryCodeComponent: ForwardRefRenderFunction<
  MfaRecoveryCodeRef,
  MfaRecoveryCodeProps
> = ({ recoveryCodes }, ref) => {
  const printComponentRef = useRef<HTMLDivElement>(null);
  const printRef = useRef(
    useReactToPrint({
      content: () => printComponentRef.current,
    }),
  );

  useImperativeHandle(
    ref,
    () => ({
      print: () => {
        printRef?.current?.();
      },
      download: () => {
        const recoveryCodesHref = `data:text/plain;charset=utf-8,${encodeURIComponent(
          recoveryCodes.join('\n'),
        )}`;
        downloadWithLink('hotjar_recovery_codes.txt', recoveryCodesHref);
      },
    }),
    [recoveryCodes],
  );

  return (
    <RecoveryCodesBox ref={printComponentRef}>
      {recoveryCodes && recoveryCodes.map((rc) => <div key={rc}>{rc}</div>)}
    </RecoveryCodesBox>
  );
};

export const MfaRecoveryCode = forwardRef(MfaRecoveryCodeComponent);
